import { cn } from '@/lib/utils'

/**
 * The words and colours this module renders codes as.
 *
 * The server serves the same vocabulary at `/nominations/memberships/vocabulary` and that is
 * the authority for WHICH codes exist. What lives here is presentation — a colour is not
 * something an API should dictate — with a fallback that titles any code this file has not
 * been taught, so a new status renders as words rather than as nothing.
 */

const NOMINATION_STATUS: Record<string, { label: string; tone: string }> = {
  DRAFT: { label: 'Draft', tone: 'bg-muted text-muted-foreground' },
  ROLL_OPEN: {
    label: 'Signing up members',
    tone: 'bg-sky-100 text-sky-900 dark:bg-sky-500/15 dark:text-sky-200',
  },
  ROLL_FROZEN: {
    label: 'Roll frozen',
    tone: 'bg-indigo-100 text-indigo-900 dark:bg-indigo-500/15 dark:text-indigo-200',
  },
  VOTING: {
    label: 'Voting open',
    tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  CLOSED: { label: 'Voting closed', tone: 'bg-muted text-muted-foreground' },
  TALLIED: {
    label: 'Counted',
    tone: 'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200',
  },
  AWARDED: {
    label: 'Ticket awarded',
    tone: 'bg-chocolate-soft/50 text-chocolate',
  },
  DISPUTED: {
    label: 'Disputed',
    tone: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  },
  CANCELLED: { label: 'Cancelled', tone: 'bg-muted text-muted-foreground line-through' },
  SUPERSEDED: { label: 'Superseded', tone: 'bg-muted text-muted-foreground' },
}

const ASPIRANT_STATUS: Record<string, { label: string; tone: string }> = {
  APPLIED: { label: 'Applied', tone: 'bg-muted text-muted-foreground' },
  FEE_PAID: {
    label: 'Fee paid',
    tone: 'bg-sky-100 text-sky-900 dark:bg-sky-500/15 dark:text-sky-200',
  },
  VETTING: {
    label: 'In vetting',
    tone: 'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200',
  },
  CLEARED: {
    label: 'Cleared to stand',
    tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  REJECTED: {
    label: 'Rejected',
    tone: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  },
  WITHDRAWN: { label: 'Withdrawn', tone: 'bg-muted text-muted-foreground line-through' },
}

const MEMBERSHIP_STATUS: Record<string, { label: string; tone: string }> = {
  ACTIVE: {
    label: 'Active',
    tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  LEFT: { label: 'Left', tone: 'bg-muted text-muted-foreground' },
  SUSPENDED: {
    label: 'Suspended',
    tone: 'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200',
  },
  EXPELLED: {
    label: 'Expelled',
    tone: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  },
}

/** Titles any code this file has not been taught. A new status renders, never blanks. */
const title = (code: string) => {
  const spaced = code.replace(/_/g, ' ').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function Chip({ label, tone }: { label: string; tone: string }) {
  return (
    <span className={cn('inline-flex items-center rounded px-1.5 py-0.5 text-erp-xs font-medium',
      tone)}>
      {label}
    </span>
  )
}

export function NominationStatusChip({ code }: { code: string }) {
  const it = NOMINATION_STATUS[code] ?? { label: title(code), tone: 'bg-muted text-muted-foreground' }
  return <Chip label={it.label} tone={it.tone} />
}

export function AspirantStatusChip({ code }: { code: string }) {
  const it = ASPIRANT_STATUS[code] ?? { label: title(code), tone: 'bg-muted text-muted-foreground' }
  return <Chip label={it.label} tone={it.tone} />
}

export function MembershipStatusChip({ code }: { code: string }) {
  const it = MEMBERSHIP_STATUS[code] ?? { label: title(code), tone: 'bg-muted text-muted-foreground' }
  return <Chip label={it.label} tone={it.tone} />
}

export const nominationStatusLabel = (code: string) =>
  NOMINATION_STATUS[code]?.label ?? title(code)

export const methodLabel = (code: string) => ({
  UNIVERSAL_MEMBER: 'Every member votes',
  DELEGATE: 'Delegates only',
  CONSENSUS: 'Agreed without a vote',
  DIRECT_TICKET: 'Ticket issued directly',
}[code] ?? title(code))

export const channelLabel = (code: string) => ({
  ONLINE: 'Online',
  USSD: 'USSD',
  ASSISTED: 'Assisted in person',
  NONE: 'Not voted',
}[code] ?? title(code))

export const seatLabel = (code: string) => ({
  PRESIDENT: 'President',
  GOVERNOR: 'Governor',
  SENATOR: 'Senator',
  WOMAN_REP: 'Woman Representative',
  MP: 'Member of Parliament',
  MCA: 'Member of County Assembly',
}[code] ?? title(code))

/** The level a seat is contested at — the server's SeatTypeRules, mirrored for the picker. */
export const levelForSeat = (seat: string) => ({
  PRESIDENT: 'COUNTRY', GOVERNOR: 'COUNTY', SENATOR: 'COUNTY',
  WOMAN_REP: 'COUNTY', MP: 'CONSTITUENCY', MCA: 'WARD',
}[seat] ?? null)

export const SEATS = ['PRESIDENT', 'GOVERNOR', 'SENATOR', 'WOMAN_REP', 'MP', 'MCA']
